const BARTER_CONTACT_RETURN_KEY = 'barter-contact-return';
const PROJECT_PLAN_HANDOFF_KEY = 'wl-project-plan-handoff-v1';
const BARTER_PROJECT_FILE_LIMIT = 5;
const BARTER_PROJECT_FILE_TOTAL_LIMIT_BYTES = 20 * 1024 * 1024;
const BARTER_ACCEPTED_BANNER_DURATION_MS = 6500;

function stashBarterContactReturn(intent) {
  try {
    window.sessionStorage.setItem(
      BARTER_CONTACT_RETURN_KEY,
      JSON.stringify({
        intent,
        returnTo: `${window.location.pathname}${window.location.search}${window.location.hash}`,
      })
    );
  } catch (_unusedError) {
    // Ignore sessionStorage failures.
  }
}

function getBarterContactResult() {
  try {
    return new URLSearchParams(window.location.search).get('barter_contact_result') || '';
  } catch (_unusedError) {
    return '';
  }
}

function clearBarterContactResult() {
  try {
    const currentUrl = new URL(window.location.href);
    if (!currentUrl.searchParams.has('barter_contact_result')) return;
    currentUrl.searchParams.delete('barter_contact_result');
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState({}, '', nextUrl);
  } catch (_unusedError) {
    // Ignore history API failures.
  }
}

if (!customElements.get('barter-builder')) {
  class BarterBuilder extends HTMLElement {
    constructor() {
      super();
      this.searchTimer = null;
      this.searchController = null;
      this.statusPollTimer = null;
      this.validationSyncTimers = [];
      this.currentSuggestedTotalCents = 0;
      this.isSubmissionLocked = false;
      this.activeBarterStatus = '';
      this.handleClick = this.handleClick.bind(this);
      this.handleInput = this.handleInput.bind(this);
      this.handleChange = this.handleChange.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.currency = this.dataset.currency || 'USD';
      this.customerId = (this.dataset.customerId || '').trim();
      this.customerEmail = (this.dataset.customerEmail || '').trim().toLowerCase();
      this.backendEndpoint = (this.dataset.backendEndpoint || '').trim();
      this.statusEndpoint = (this.dataset.statusEndpoint || '').trim();
      this.excludedHandles = (this.dataset.excludedHandles || '')
        .split(',')
        .map((handle) => handle.trim().toLowerCase())
        .filter(Boolean);
      this.selectAll('[data-role="item-quantity"]').forEach((input) => this.normalizeQuantityInput(input));
      this.addEventListener('click', this.handleClick);
      this.statusSlot = this.getStatusSlot();
      if (this.statusSlot && !this.contains(this.statusSlot)) {
        this.statusSlot.addEventListener('click', this.handleClick);
      }
      this.addEventListener('input', this.handleInput);
      this.addEventListener('change', this.handleChange);
      this.updateAddedPanel();
      this.updateSummary();
      this.hydrateProjectPlanHandoff().then((didHydratePlan) => {
        if (!didHydratePlan) this.hydrateLinkedProductFromUrl();
      });
      this.queueInitialValidationSync();
      this.recoverPendingSubmissionAfterReturn();
      this.initializeStatusState();
      this.loadBackendProposalStatus();
      this.startBackendStatusPolling();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      if (this.statusSlot && !this.contains(this.statusSlot)) {
        this.statusSlot.removeEventListener('click', this.handleClick);
      }
      this.removeEventListener('input', this.handleInput);
      this.removeEventListener('change', this.handleChange);
      this.clearValidationSyncTimers();
      this.clearBackendStatusPolling();
    }

    select(selector) {
      return this.querySelector(selector);
    }

    selectAll(selector) {
      return Array.from(this.querySelectorAll(selector));
    }

    handleClick(event) {
      const qtyButton = event.target.closest('[data-role="qty-button"]');
      if (qtyButton) {
        event.preventDefault();
        this.adjustQuantity(qtyButton);
        return;
      }

      const addButton = event.target.closest('[data-role="add-product"]');
      if (addButton) {
        event.preventDefault();
        this.addSearchProduct(addButton);
        return;
      }

      const removeButton = event.target.closest('[data-role="remove-item"]');
      if (removeButton) {
        event.preventDefault();
        const item = removeButton.closest('.wl-barter-item');
        if (item) item.remove();
        this.updateAddedPanel();
        this.updateSummary();
        return;
      }

      const counterResponseButton = event.target.closest('[data-role="counter-response-button"]');
      if (counterResponseButton && !counterResponseButton.disabled) {
        event.preventDefault();
        this.sendCounterResponse(counterResponseButton);
        return;
      }

      const copyDiscountButton = event.target.closest('[data-role="copy-discount-code"]');
      if (copyDiscountButton) {
        event.preventDefault();
        this.copyDiscountCode(copyDiscountButton);
        return;
      }

      const submitButton = event.target.closest('[data-role="submit-button"]');
      if (submitButton && !submitButton.disabled) {
        event.preventDefault();
        this.sendRequest();
      }
    }

    handleInput(event) {
      if (event.target.matches('[data-role="search-input"]')) {
        this.queueSearch(event.target.value);
        return;
      }

      if (event.target.matches('[data-role="item-quantity"]')) {
        this.normalizeQuantityInput(event.target);
        this.updateSummary();
        return;
      }

      if (event.target.matches('.wl-barter__offer-input')) {
        this.handleOfferInput(event.target);
        return;
      }

      if (event.target.matches('[data-role="item-price"]')) {
        const item = event.target.closest('.wl-barter-item');
        if (item) {
          const raw = event.target.value.replace(/[^0-9.]/g, '');
          const dollars = parseFloat(raw) || 0;
          item.dataset.unitPrice = String(Math.round(dollars * 100));
          this.updateSummary();
        }
        return;
      }

      // Re-validate button on contact field changes
      if (
        event.target.matches('[data-role="contact-name"]') ||
        event.target.matches('[data-role="contact-email"]')
      ) {
        this.updateSubmitButton();
      }
    }

    handleChange(event) {
      if (event.target.matches('[data-role="item-quantity"]')) {
        this.normalizeQuantityInput(event.target);
        this.updateSummary();
      } else if (event.target.matches('[data-role="item-select"]')) {
        this.updateSummary();
      } else if (event.target.matches('[data-role="project-files"]')) {
        this.renderProjectFileList(event.target);
        this.updateSubmitButton();
      }
    }

    // ─── Search ──────────────────────────────────────────────────────────

    queueSearch(rawValue) {
      const value = rawValue.trim();
      window.clearTimeout(this.searchTimer);

      if (value.length < 2) {
        this.abortSearch();
        this.renderSearchState('Search for a product or SKU to add it to this custom pricing request.');
        return;
      }

      this.renderSearchState('Searching products...');
      this.searchTimer = window.setTimeout(() => this.runSearch(value), 220);
    }

    async runSearch(query) {
      this.abortSearch();
      this.searchController = new AbortController();
      const searchController = this.searchController;
      const isExactSkuQuery = this.isLikelyExactSkuQuery(query);
      const normalizedQuery = this.normalizeSku(query);
      const searchQuery = isExactSkuQuery
        ? `variants.sku:"${this.escapeSearchPhrase(normalizedQuery)}"`
        : query;

      const predictiveBase = window.routes && window.routes.predictive_search_url
        ? window.routes.predictive_search_url
        : '/search/suggest';
      const jsonBase = predictiveBase.endsWith('.json') ? predictiveBase : `${predictiveBase}.json`;
      const url =
        `${jsonBase}?q=${encodeURIComponent(searchQuery)}` +
        '&resources[type]=product' +
        '&resources[limit]=8' +
        '&resources[options][unavailable_products]=hide' +
        '&resources[options][fields]=title,product_type,variants.sku,variants.title,vendor';

      try {
        const response = await fetch(url, {
          signal: searchController.signal,
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const payload = await response.json();
        const products =
          payload?.resources?.results?.products ||
          payload?.resources?.products ||
          payload?.products ||
          [];
        const resolvedProducts = isExactSkuQuery
          ? await this.resolveExactSkuProducts(normalizedQuery, products, searchController.signal)
          : products;

        if (searchController.signal.aborted || searchController !== this.searchController) return;
        this.renderSearchResults(resolvedProducts);
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        this.renderSearchState('Search is unavailable right now. Try again in a moment.');
      }
    }

    normalizeSku(value) {
      return String(value || '')
        .normalize('NFKC')
        .replace(/[‐‑‒–—−]/g, '-')
        .replace(/\s*([\/-])\s*/g, '$1')
        .replace(/\s+/g, ' ')
        .trim()
        .toUpperCase();
    }

    isLikelyExactSkuQuery(value) {
      const normalized = this.normalizeSku(value);
      if (!/[A-Z]/.test(normalized) || !/[0-9]/.test(normalized)) return false;
      const separatorCount = (normalized.match(/[\-/()]/g) || []).length;
      return separatorCount >= 2 || (separatorCount >= 1 && normalized.length >= 9);
    }

    escapeSearchPhrase(value) {
      return String(value || '').replace(/\\/g, '\\\\').replace(/"/g, '\\"');
    }

    async resolveExactSkuProducts(normalizedQuery, products, signal) {
      const resolvedProducts = await Promise.all(
        products.map(async (product) => {
          const handle = (product?.handle || '').trim();
          if (!handle) return null;

          try {
            const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
              signal,
              headers: { Accept: 'application/json' },
            });
            if (!response.ok) return null;

            const productDetails = await response.json();
            const variants = Array.isArray(productDetails?.variants) ? productDetails.variants : [];
            const matchedVariant = variants.find(
              (variant) => variant.available !== false && this.normalizeSku(variant.sku) === normalizedQuery
            );
            if (!matchedVariant) return null;

            return {
              ...product,
              id: productDetails.id || product.id,
              handle: productDetails.handle || product.handle,
              title: productDetails.title || product.title,
              vendor: productDetails.vendor || product.vendor,
              price: matchedVariant.price,
              _wlMatchedVariant: matchedVariant,
            };
          } catch (error) {
            if (error.name === 'AbortError') throw error;
            console.warn(`Exact SKU verification failed for ${handle}.`, error);
            return null;
          }
        })
      );

      return resolvedProducts.filter(Boolean);
    }

    abortSearch() {
      if (this.searchController) {
        this.searchController.abort();
        this.searchController = null;
      }
    }

    getLinkedProductRequest() {
      try {
        const params = new URLSearchParams(window.location.search);
        const rawHandle = (params.get('product') || params.get('handle') || '').trim().toLowerCase();
        const handle = rawHandle.replace(/[^a-z0-9-]/g, '');
        const variantId = (params.get('variant') || '').trim().replace(/[^0-9]/g, '');
        return handle ? { handle, variantId } : null;
      } catch (_unusedError) {
        return null;
      }
    }

    getProjectPlanHandoff() {
      try {
        const params = new URLSearchParams(window.location.search);
        if (params.get('project_plan') !== '1') return null;
        const payload = JSON.parse(window.sessionStorage.getItem(PROJECT_PLAN_HANDOFF_KEY) || 'null');
        if (!payload || payload.schemaVersion !== 1 || payload.source !== 'project_planner') return null;
        const sourceProduct = String(payload.sourceProduct || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
        const lines = Array.isArray(payload.lines) ? payload.lines.slice(0, 25) : [];
        const validatedLines = lines
          .map((line) => ({
            handle: String(line?.handle || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, ''),
            variantId: String(line?.variantId || '').replace(/[^0-9]/g, ''),
            quantity: Math.min(9999, Math.max(1, parseInt(line?.quantity || '1', 10) || 1)),
            lineSource: line?.source === 'primary' ? 'primary' : 'optional_project_extra',
            commerceMode: ['cart', 'quote_only', 'freight_quote', 'unsupported'].includes(line?.commerceMode)
              ? line.commerceMode
              : 'unsupported',
          }))
          .filter((line) => line.handle && /^\d+$/.test(line.variantId));
        return validatedLines.length && sourceProduct ? { sourceProduct, lines: validatedLines } : null;
      } catch (_unusedError) {
        return null;
      }
    }

    async hydrateProjectPlanHandoff() {
      const handoff = this.getProjectPlanHandoff();
      if (!handoff) {
        try {
          if (new URLSearchParams(window.location.search).get('project_plan') === '1') {
            this.renderSearchState('The project list could not be restored. Add products manually by name or exact SKU.');
          }
        } catch (_unusedError) {
          // Ignore URL parsing failures.
        }
        return false;
      }
      const { lines, sourceProduct } = handoff;

      const failures = [];
      for (const line of lines) {
        try {
          const response = await fetch(`/products/${encodeURIComponent(line.handle)}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error(`Product lookup failed: ${response.status}`);
          const product = await response.json();
          const added = this.addLinkedProduct(
            product,
            line.variantId,
            line.quantity,
            'project-planner',
            true,
            sourceProduct,
            line.lineSource,
            line.commerceMode
          );
          if (!added) failures.push(line.handle);
        } catch (error) {
          console.error(error);
          failures.push(line.handle);
        }
      }

      try {
        window.sessionStorage.removeItem(PROJECT_PLAN_HANDOFF_KEY);
      } catch (_unusedError) {
        // Ignore storage cleanup failures.
      }

      if (failures.length) {
        this.renderSearchState(`Project list loaded with ${failures.length} item${failures.length === 1 ? '' : 's'} needing manual review.`);
      } else {
        this.renderSearchState(`Project list loaded with ${lines.length} exact line${lines.length === 1 ? '' : 's'}. Review quantities before submitting.`);
      }
      return true;
    }

    async hydrateLinkedProductFromUrl() {
      if (this.linkedProductHydrated) return;
      const linkedProduct = this.getLinkedProductRequest();
      if (!linkedProduct) return;

      this.linkedProductHydrated = true;

      try {
        const response = await fetch(`/products/${encodeURIComponent(linkedProduct.handle)}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) throw new Error(`Product lookup failed: ${response.status}`);
        const product = await response.json();
        this.addLinkedProduct(product, linkedProduct.variantId);
      } catch (error) {
        console.error(error);
        this.renderSearchState('We could not add that product automatically. Search by product name or SKU to add it.');
      }
    }

    renderSearchState(message) {
      const results = this.select('[data-role="search-results"]');
      if (!results) return;
      results.innerHTML = `<div class="wl-barter__search-empty">${this.escapeHtml(message)}</div>`;
    }

    renderSearchResults(products) {
      const results = this.select('[data-role="search-results"]');
      if (!results) return;

      const eligibleProducts = products.filter((product) => !this.isExcludedProduct(product));

      if (!eligibleProducts.length) {
        this.renderSearchState('No matching products found. Try a broader name or SKU.');
        return;
      }

      results.innerHTML = eligibleProducts
        .map((product) => {
          const matchedVariant = product._wlMatchedVariant || null;
          const productId = product.id || product.handle || product.title;
          const image =
            matchedVariant?.featured_image?.src ||
            matchedVariant?.featured_image?.url ||
            product.featured_image?.url ||
            product.featured_image ||
            product.image?.url ||
            product.image ||
            '';
          const url = matchedVariant
            ? this.buildLinkedProductUrl(product.handle, matchedVariant.id)
            : product.url || (product.handle ? `/products/${product.handle}` : '#');
          const vendor = product.vendor || '';
          const title = product.title || 'Untitled product';
          const handle = product.handle || '';
          const variantId = matchedVariant?.id || '';
          const variantTitle = matchedVariant?.title && matchedVariant.title !== 'Default Title'
            ? matchedVariant.title
            : '';
          const sku = matchedVariant?.sku || '';
          const subline = [vendor, sku].filter(Boolean).join(' - ');
          const price = this.normalizePrice(matchedVariant?.price ?? product.price);
          const placeholder = title.slice(0, 2).toUpperCase();

          return `
            <article class="wl-barter__search-card">
              ${
                image
                  ? `<div class="wl-barter__search-card-image"><img src="${this.escapeAttribute(image)}" alt="${this.escapeAttribute(title)}" loading="lazy" width="72" height="72"></div>`
                  : `<div class="wl-barter__search-thumb-placeholder">${this.escapeHtml(placeholder)}</div>`
              }
              <div class="wl-barter__search-card-meta">
                <h3 class="wl-barter__search-card-title"><a href="${this.escapeAttribute(url)}">${this.escapeHtml(title)}</a></h3>
                ${subline ? `<p class="wl-barter__search-card-sub">${this.escapeHtml(subline)}</p>` : ''}
                <p class="wl-barter__search-card-price">${this.formatMoney(price)} <span>each</span></p>
              </div>
              <button
                type="button"
                class="wl-barter__search-add"
                data-role="add-product"
                data-product-id="${this.escapeAttribute(String(productId))}"
                data-title="${this.escapeAttribute(title)}"
                data-handle="${this.escapeAttribute(handle)}"
                data-vendor="${this.escapeAttribute(vendor)}"
                data-variant-id="${this.escapeAttribute(String(variantId))}"
                data-variant-title="${this.escapeAttribute(variantTitle)}"
                data-sku="${this.escapeAttribute(sku)}"
                data-url="${this.escapeAttribute(url)}"
                data-image="${this.escapeAttribute(image)}"
                data-list-price="${this.escapeAttribute(String(price))}"
                data-unit-price="${this.escapeAttribute(String(price))}"
              >
                Add to request
              </button>
            </article>
          `;
        })
        .join('');
    }

    // ─── Item Management ──────────────────────────────────────────────────

    addSearchProduct(button) {
      const productId = button.dataset.productId;
      const handle = (button.dataset.handle || '').trim().toLowerCase();
      const variantId = button.dataset.variantId || '';

      if (this.isExcludedHandle(handle)) {
        this.renderSearchState('That product is not eligible for custom pricing requests.');
        return;
      }

      const existing = this.findItemByProductAndVariant(productId, variantId);

      if (existing) {
        const checkbox = existing.querySelector('[data-role="item-select"]');
        const quantityInput = existing.querySelector('[data-role="item-quantity"]');
        if (checkbox) checkbox.checked = true;
        if (quantityInput) {
          quantityInput.value = Math.max(1, parseInt(quantityInput.value || '1', 10) + 1);
        }
        this.updateSummary();
        return;
      }

      const addedItems = this.select('[data-role="added-items"]');
      if (!addedItems) return;

      const title = button.dataset.title || 'Untitled product';
      const vendor = button.dataset.vendor || '';
      const variantTitle = button.dataset.variantTitle || '';
      const sku = button.dataset.sku || '';
      const subline = [vendor, variantTitle, sku].filter(Boolean).join(' - ');
      const url = button.dataset.url || '#';
      const image = button.dataset.image || '';
      const unitPrice = this.normalizePrice(button.dataset.unitPrice);
      const placeholder = title.slice(0, 2).toUpperCase();

      addedItems.insertAdjacentHTML(
        'beforeend',
        `
          <li
            class="wl-barter-item"
            data-product-id="${this.escapeAttribute(productId)}"
            data-product-handle="${this.escapeAttribute(handle)}"
            data-variant-id="${this.escapeAttribute(variantId)}"
            data-variant-title="${this.escapeAttribute(variantTitle)}"
            data-sku="${this.escapeAttribute(sku)}"
            data-product-url="${this.escapeAttribute(url)}"
            data-image-url="${this.escapeAttribute(image)}"
            data-source="added"
            data-list-price="${this.escapeAttribute(String(unitPrice))}"
            data-unit-price="${this.escapeAttribute(String(unitPrice))}"
          >
            <div class="wl-barter-item__layout">
              <label class="wl-barter-item__select" aria-label="Include ${this.escapeAttribute(title)} in custom pricing request">
                <input type="checkbox" checked data-role="item-select">
              </label>
              ${
                image
                  ? `<div class="wl-barter-item__image"><img src="${this.escapeAttribute(image)}" alt="${this.escapeAttribute(title)}" loading="lazy" width="72" height="72"></div>`
                  : `<div class="wl-barter-item__image-placeholder">${this.escapeHtml(placeholder)}</div>`
              }
              <div class="wl-barter-item__meta">
                <div class="wl-barter-item__head">
                  <div>
                    <h3 class="wl-barter-item__title"><a href="${this.escapeAttribute(url)}">${this.escapeHtml(title)}</a></h3>
                    ${subline ? `<p class="wl-barter-item__subline">${this.escapeHtml(subline)}</p>` : ''}
                  </div>
                  <span class="wl-barter-item__tag">Added here</span>
                </div>
                <div class="wl-barter-item__controls">
                  <div class="wl-barter-item__prices">
                    <div class="wl-barter-item__list-price" aria-label="List price">
                      <span class="wl-barter-item__list-label">List</span>
                      <span class="wl-barter-item__list-amount">${this.formatMoney(unitPrice)}</span>
                    </div>
                    <svg class="wl-barter-item__prices-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2.5 7h9M8 3.5L11.5 7 8 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <div class="wl-barter-item__price-field" aria-label="Your price — editable">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" class="wl-barter-item__price-edit-icon">
                        <path d="M8.5 1.5a1.414 1.414 0 0 1 2 2L3.5 10.5l-2.5.5.5-2.5L8.5 1.5Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <input type="text" class="wl-barter-item__price-input" data-role="item-price" value="${this.escapeAttribute((unitPrice / 100).toFixed(2))}" inputmode="decimal" aria-label="Your price per item">
                      <span class="wl-barter-item__price-unit">/ ea</span>
                    </div>
                  </div>
                  <div class="wl-barter-item__qty" aria-label="Quantity">
                    <button type="button" class="wl-barter-item__qty-button" data-role="qty-button" data-direction="-1" aria-label="Decrease quantity">−</button>
                    <input class="wl-barter-item__qty-input" type="number" min="1" step="1" value="1" data-role="item-quantity" inputmode="numeric">
                    <button type="button" class="wl-barter-item__qty-button" data-role="qty-button" data-direction="1" aria-label="Increase quantity">+</button>
                  </div>
                  <button type="button" class="wl-barter-item__remove" data-role="remove-item">Remove</button>
                </div>
              </div>
            </div>
          </li>
        `
      );

      this.updateAddedPanel();
      this.updateSummary();
    }

    addLinkedProduct(
      product,
      variantId = '',
      quantity = 1,
      source = 'product-page',
      requireExactVariant = false,
      projectSourceProduct = '',
      projectLineSource = '',
      projectCommerceMode = ''
    ) {
      if (!product) return false;

      const variants = Array.isArray(product.variants) ? product.variants : [];
      const exactVariant = variants.find((variant) => String(variant.id) === String(variantId));
      if (requireExactVariant && !exactVariant) return false;
      const selectedVariant =
        exactVariant ||
        variants.find((variant) => variant.available !== false) ||
        variants[0] ||
        {};
      const productId = product.id || product.handle || product.title;
      const handle = (product.handle || '').trim().toLowerCase();

      if (this.isExcludedHandle(handle)) {
        this.renderSearchState('That product is not eligible for custom pricing requests.');
        return false;
      }

      const existing = this.findItemByProductAndVariant(productId, selectedVariant.id || variantId);
      if (existing) {
        const checkbox = existing.querySelector('[data-role="item-select"]');
        const quantityInput = existing.querySelector('[data-role="item-quantity"]');
        if (checkbox) checkbox.checked = true;
        if (quantityInput) quantityInput.value = String(Math.min(9999, Math.max(1, parseInt(quantity || '1', 10) || 1)));
        this.updateSummary();
        return true;
      }

      const addedItems = this.select('[data-role="added-items"]');
      if (!addedItems) return false;

      const title = product.title || 'Untitled product';
      const vendor = product.vendor || '';
      const variantTitle = selectedVariant.title && selectedVariant.title !== 'Default Title'
        ? selectedVariant.title
        : '';
      const subline = [vendor, variantTitle].filter(Boolean).join(' - ');
      const variantValue = selectedVariant.id || variantId || '';
      const price = this.normalizePrice(selectedVariant.price || product.price);
      const image =
        selectedVariant.featured_image?.src ||
        selectedVariant.featured_image?.url ||
        product.featured_image ||
        product.images?.[0] ||
        '';
      const productUrl = this.buildLinkedProductUrl(handle, variantValue);
      const placeholder = title.slice(0, 2).toUpperCase();

      addedItems.insertAdjacentHTML(
        'beforeend',
        `
          <li
            class="wl-barter-item"
            data-product-id="${this.escapeAttribute(String(productId))}"
            data-product-handle="${this.escapeAttribute(handle)}"
            data-variant-id="${this.escapeAttribute(String(variantValue))}"
            data-variant-title="${this.escapeAttribute(variantTitle)}"
            data-sku="${this.escapeAttribute(selectedVariant.sku || '')}"
            data-product-url="${this.escapeAttribute(productUrl)}"
            data-image-url="${this.escapeAttribute(image)}"
            data-source="${this.escapeAttribute(source)}"
            data-project-source-product="${this.escapeAttribute(projectSourceProduct)}"
            data-project-line-source="${this.escapeAttribute(projectLineSource)}"
            data-project-commerce-mode="${this.escapeAttribute(projectCommerceMode)}"
            data-list-price="${this.escapeAttribute(String(price))}"
            data-unit-price="${this.escapeAttribute(String(price))}"
          >
            <div class="wl-barter-item__layout">
              <label class="wl-barter-item__select" aria-label="Include ${this.escapeAttribute(title)} in custom pricing request">
                <input type="checkbox" checked data-role="item-select">
              </label>
              ${
                image
                  ? `<div class="wl-barter-item__image"><img src="${this.escapeAttribute(image)}" alt="${this.escapeAttribute(title)}" loading="lazy" width="72" height="72"></div>`
                  : `<div class="wl-barter-item__image-placeholder">${this.escapeHtml(placeholder)}</div>`
              }
              <div class="wl-barter-item__meta">
                <div class="wl-barter-item__head">
                  <div>
                    <h3 class="wl-barter-item__title"><a href="${this.escapeAttribute(productUrl)}">${this.escapeHtml(title)}</a></h3>
                    ${subline ? `<p class="wl-barter-item__subline">${this.escapeHtml(subline)}</p>` : ''}
                  </div>
                  <span class="wl-barter-item__tag">From product</span>
                </div>
                <div class="wl-barter-item__controls">
                  <div class="wl-barter-item__prices">
                    <div class="wl-barter-item__list-price" aria-label="List price">
                      <span class="wl-barter-item__list-label">List</span>
                      <span class="wl-barter-item__list-amount">${this.formatMoney(price)}</span>
                    </div>
                    <svg class="wl-barter-item__prices-arrow" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                      <path d="M2.5 7h9M8 3.5L11.5 7 8 10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <div class="wl-barter-item__price-field" aria-label="Your price - editable">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true" class="wl-barter-item__price-edit-icon">
                        <path d="M8.5 1.5a1.414 1.414 0 0 1 2 2L3.5 10.5l-2.5.5.5-2.5L8.5 1.5Z" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                      <input type="text" class="wl-barter-item__price-input" data-role="item-price" value="${this.escapeAttribute((price / 100).toFixed(2))}" inputmode="decimal" aria-label="Your price per item">
                      <span class="wl-barter-item__price-unit">/ ea</span>
                    </div>
                  </div>
                  <div class="wl-barter-item__qty" aria-label="Quantity">
                    <button type="button" class="wl-barter-item__qty-button" data-role="qty-button" data-direction="-1" aria-label="Decrease quantity">−</button>
                    <input class="wl-barter-item__qty-input" type="number" min="1" max="9999" step="1" value="${this.escapeAttribute(String(Math.min(9999, Math.max(1, parseInt(quantity || '1', 10) || 1))))}" data-role="item-quantity" inputmode="numeric">
                    <button type="button" class="wl-barter-item__qty-button" data-role="qty-button" data-direction="1" aria-label="Increase quantity">+</button>
                  </div>
                  <button type="button" class="wl-barter-item__remove" data-role="remove-item">Remove</button>
                </div>
              </div>
            </div>
          </li>
        `
      );

      this.updateAddedPanel();
      this.updateSummary();
      return true;
    }

    buildLinkedProductUrl(handle, variantId = '') {
      if (!handle) return '#';
      const url = new URL(`/products/${handle}`, window.location.origin);
      if (variantId) url.searchParams.set('variant', variantId);
      return `${url.pathname}${url.search}`;
    }

    adjustQuantity(button) {
      const input = button.closest('.wl-barter-item')?.querySelector('[data-role="item-quantity"]');
      if (!input) return;
      const direction = parseInt(button.dataset.direction || '0', 10);
      const currentValue = Math.max(1, parseInt(input.value || '1', 10));
      input.value = Math.max(1, currentValue + direction);
      this.updateSummary();
    }

    normalizeQuantityInput(input) {
      if (!input || !input.matches('[data-role="item-quantity"]')) return;
      const numericValue = parseInt(input.value || '1', 10);
      input.value = Number.isNaN(numericValue) || numericValue < 1 ? 1 : numericValue;
    }

    findItemByProductId(productId) {
      return this.selectAll('.wl-barter-item').find((item) => item.dataset.productId === String(productId));
    }

    findItemByProductAndVariant(productId, variantId = '') {
      return this.selectAll('.wl-barter-item').find(
        (item) =>
          item.dataset.productId === String(productId) &&
          (!variantId || !item.dataset.variantId || item.dataset.variantId === String(variantId))
      );
    }

    isExcludedHandle(handle) {
      return Boolean(handle) && this.excludedHandles.includes(handle);
    }

    isExcludedProduct(product) {
      return this.isExcludedHandle((product?.handle || '').trim().toLowerCase());
    }

    // ─── Summary + Button State ───────────────────────────────────────────

    updateAddedPanel() {
      const panel = this.select('[data-role="added-panel"]');
      const addedItems = this.select('[data-role="added-items"]');
      if (!panel || !addedItems) return;
      panel.hidden = addedItems.children.length === 0;
    }

    updateSummary() {
      const summaryItems = [];
      let currentPrice = 0;
      let selectedQuantity = 0;

      this.selectAll('.wl-barter-item').forEach((item) => {
        const checkbox = item.querySelector('[data-role="item-select"]');
        const quantityInput = item.querySelector('[data-role="item-quantity"]');
        const isSelected = checkbox ? checkbox.checked : true;
        const quantity = quantityInput ? Math.max(1, parseInt(quantityInput.value || '1', 10)) : 1;
        const unitPrice = this.normalizePrice(item.dataset.unitPrice);
        if (!isSelected) return;

        const title = item.querySelector('.wl-barter-item__title')?.textContent?.trim() || 'Product';
        currentPrice += unitPrice * quantity;
        selectedQuantity += quantity;
        summaryItems.push({ title, quantity, unitPrice, totalPrice: unitPrice * quantity });
      });

      this.selectAll('[data-role="current-price"]').forEach((node) => {
        node.textContent = this.formatMoney(currentPrice);
      });

      this.selectAll('[data-role="selected-count"]').forEach((node) => {
        node.textContent = String(selectedQuantity);
      });

      this.currentSuggestedTotalCents = currentPrice;
      this.syncOfferInputFromSummary();

      const summaryContainer = this.select('[data-role="summary-items"]');
      if (summaryContainer) {
        if (!summaryItems.length) {
          summaryContainer.innerHTML =
            '<div class="wl-barter__summary-empty">Select items from the cart or add products from search to build a custom pricing request.</div>';
        } else {
          summaryContainer.innerHTML = summaryItems
            .map(
              (item) => `
                <div class="wl-barter__summary-item">
                  <div class="wl-barter__summary-item-title">
                    <span>${this.escapeHtml(item.title)}</span>
                    <span>${this.formatMoney(item.totalPrice)}</span>
                  </div>
                  <div class="wl-barter__summary-item-meta">
                    ${item.quantity} × ${this.formatMoney(item.unitPrice)} each
                  </div>
                </div>
              `
            )
            .join('');
        }
      }

      this.updateSubmitButton();
    }

    updateSubmitButton() {
      const button = this.select('[data-role="submit-button"]');
      if (!button) return;

      if (this.isSubmissionLocked) {
        this.syncLockedButtonLabel(button);
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.title = 'You already have an active custom pricing request.';
        return;
      }

      const validation = this.getSubmitValidation();
      button.disabled = !validation.isReady;
      button.setAttribute('aria-disabled', String(!validation.isReady));
      button.title = validation.isReady
        ? ''
        : `Missing: ${validation.missingRequirements.join(', ')}`;

      if (validation.isReady) {
        button.textContent = 'Send custom pricing request';
        this.hideStatusIfNotSuccess();
      } else {
        this.showStatus(
          'info',
          `Complete these before sending: ${this.formatRequirementList(validation.missingRequirements)}.`
        );
      }
    }

    isValidEmail(value) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || '').trim());
    }

    getSubmitValidation() {
      const hasItems = this.selectAll('.wl-barter-item').some((item) => {
        const cb = item.querySelector('[data-role="item-select"]');
        return cb ? cb.checked : true;
      });

      const offerState = this.getOfferInputState();

      const nameInput = this.select('[data-role="contact-name"]');
      const emailInput = this.select('[data-role="contact-email"]');
      const emailValue = (emailInput?.value || '').trim();
      const hasName = (nameInput?.value || '').trim().length > 0;
      const hasEmail = emailValue.length > 0;
      const hasValidEmail = hasEmail && this.isValidEmail(emailValue);
      const fileValidation = this.getProjectFileValidation();

      const missingRequirements = [];

      if (!hasItems) missingRequirements.push('select at least one item');
      if (!offerState.isValid) missingRequirements.push(offerState.message);
      if (!hasName) missingRequirements.push('enter your full name');
      if (!hasEmail) {
        missingRequirements.push('enter your email address');
      } else if (!hasValidEmail) {
        missingRequirements.push('enter a valid email address');
      }
      if (!fileValidation.isValid) missingRequirements.push(fileValidation.message);

      return {
        isReady: missingRequirements.length === 0,
        missingRequirements,
      };
    }

    getProjectFiles(fileInput = this.select('[data-role="project-files"]')) {
      return Array.from(fileInput?.files || []).filter((file) => file && file.size > 0);
    }

    getProjectFileSummaries(fileInput = this.select('[data-role="project-files"]')) {
      const files = this.getProjectFiles(fileInput);

      return files.map((file) => ({
        name: file.name || 'Untitled file',
        size_bytes: file.size || 0,
        size: this.formatFileSize(file.size || 0),
        type: file.type || this.getFileExtension(file.name),
      }));
    }

    getProjectFileValidation(fileInput = this.select('[data-role="project-files"]')) {
      const files = this.getProjectFiles(fileInput);
      const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);

      if (files.length && !this.customerId) {
        return {
          isValid: false,
          message: 'sign in before uploading project files',
        };
      }

      if (files.length > BARTER_PROJECT_FILE_LIMIT) {
        return {
          isValid: false,
          message: `choose ${BARTER_PROJECT_FILE_LIMIT} or fewer project files`,
        };
      }

      if (totalSize > BARTER_PROJECT_FILE_TOTAL_LIMIT_BYTES) {
        return {
          isValid: false,
          message: `keep project files under ${this.formatFileSize(BARTER_PROJECT_FILE_TOTAL_LIMIT_BYTES)} total`,
        };
      }

      return { isValid: true, message: '' };
    }

    renderProjectFileList(fileInput = this.select('[data-role="project-files"]')) {
      const fileList = this.select('[data-role="project-file-list"]');
      if (!fileList) return;

      const files = this.getProjectFileSummaries(fileInput);
      if (!files.length) {
        fileList.hidden = true;
        fileList.innerHTML = '';
        return;
      }

      const validation = this.getProjectFileValidation(fileInput);
      fileList.hidden = false;
      fileList.innerHTML = files
        .map(
          (file) => `
            <li class="wl-barter__file-item">
              <span class="wl-barter__file-item-name">${this.escapeHtml(file.name)}</span>
              <span class="wl-barter__file-item-meta">${this.escapeHtml(file.size)}</span>
            </li>
          `
        )
        .join('');

      if (!validation.isValid) {
        fileList.insertAdjacentHTML(
          'beforeend',
          `<li class="wl-barter__file-item wl-barter__file-item--error">${this.escapeHtml(validation.message)}.</li>`
        );
      }
    }

    getOfferInputState() {
      const offerInput = this.select('.wl-barter__offer-input');
      const rawValue = (offerInput?.value || '').trim();
      const isManual = offerInput?.dataset.offerSource === 'manual';
      const parsedCents = this.parseMoneyInputToCents(rawValue);

      if (parsedCents > 0) {
        return {
          cents: parsedCents,
          isValid: true,
          message: '',
        };
      }

      if (!isManual && rawValue === '' && this.currentSuggestedTotalCents > 0) {
        return {
          cents: this.currentSuggestedTotalCents,
          isValid: true,
          message: '',
        };
      }

      return {
        cents: 0,
        isValid: false,
        message: rawValue === '' ? 'enter a target total' : 'enter a valid target total',
      };
    }

    getResolvedOfferCents() {
      return this.getOfferInputState().cents;
    }

    handleOfferInput(input) {
      if (!input) return;

      input.dataset.offerSource = 'manual';
      this.updateOfferHint();
      this.updateSubmitButton();
    }

    syncOfferInputFromSummary() {
      const offerInput = this.select('.wl-barter__offer-input');
      if (!offerInput) return;

      const rawValue = (offerInput.value || '').trim();
      const shouldAutoFill =
        this.currentSuggestedTotalCents > 0 &&
        offerInput.dataset.offerSource !== 'manual' &&
        (rawValue === '' || offerInput.dataset.offerSource === 'auto');

      if (shouldAutoFill) {
        offerInput.value = (this.currentSuggestedTotalCents / 100).toFixed(2);
        offerInput.dataset.offerSource = 'auto';
      } else if (this.currentSuggestedTotalCents <= 0 && offerInput.dataset.offerSource === 'auto') {
        offerInput.value = '';
        delete offerInput.dataset.offerSource;
      }

      this.updateOfferHint();
    }

    updateOfferHint() {
      const offerHint = this.select('[data-role="offer-hint"]');
      const offerInput = this.select('.wl-barter__offer-input');
      if (!offerHint || !offerInput) return;

      if (offerInput.dataset.offerSource === 'auto' && this.currentSuggestedTotalCents > 0) {
        offerHint.hidden = false;
        offerHint.textContent =
          `Auto-filled from item pricing: ${this.formatMoney(this.currentSuggestedTotalCents)}. You can override this total.`;
      } else {
        offerHint.hidden = true;
        offerHint.textContent = '';
      }
    }

    formatRequirementList(requirements) {
      if (!requirements.length) return '';
      if (requirements.length === 1) return requirements[0];
      if (requirements.length === 2) return `${requirements[0]} and ${requirements[1]}`;
      return `${requirements.slice(0, -1).join(', ')}, and ${requirements[requirements.length - 1]}`;
    }

    syncLockedButtonLabel(button = this.select('[data-role="submit-button"]')) {
      if (!button) return;

      if (this.activeBarterStatus === 'pending') {
        button.textContent = 'Request sent';
      } else if (this.activeBarterStatus === 'counter') {
        button.textContent = 'Counter-offer active';
      } else if (this.activeBarterStatus === 'accepted') {
        button.textContent = 'Custom pricing approved';
      } else {
        button.textContent = 'Custom pricing request active';
      }
    }

    queueInitialValidationSync() {
      this.clearValidationSyncTimers();
      [0, 250, 1000].forEach((delay) => {
        this.validationSyncTimers.push(window.setTimeout(() => this.updateSubmitButton(), delay));
      });
    }

    clearValidationSyncTimers() {
      this.validationSyncTimers.forEach((timer) => window.clearTimeout(timer));
      this.validationSyncTimers = [];
    }

    getPendingSubmissionDraftKey() {
      const storageKey = this.getPendingStorageKey();
      return storageKey ? `${storageKey}:draft` : '';
    }

    stashPendingSubmission(payload) {
      const storageKey = this.getPendingSubmissionDraftKey();
      if (!storageKey) return;

      try {
        window.sessionStorage.setItem(storageKey, JSON.stringify(payload));
      } catch (_unusedError) {
        // Ignore sessionStorage failures.
      }
    }

    readStashedPendingSubmission() {
      const storageKey = this.getPendingSubmissionDraftKey();
      if (!storageKey) return null;

      try {
        const rawValue = window.sessionStorage.getItem(storageKey);
        return rawValue ? JSON.parse(rawValue) : null;
      } catch (_unusedError) {
        return null;
      }
    }

    clearStashedPendingSubmission() {
      const storageKey = this.getPendingSubmissionDraftKey();
      if (!storageKey) return;

      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (_unusedError) {
        // Ignore sessionStorage failures.
      }
    }

    recoverPendingSubmissionAfterReturn() {
      const statusEl = this.select('[data-role="submit-status"]');
      const stashedPayload = this.readStashedPendingSubmission();
      if (!statusEl) {
        if (stashedPayload) this.clearStashedPendingSubmission();
        return;
      }

      const statusType = statusEl.dataset.statusType || '';
      const redirectedResult = getBarterContactResult();
      if ((statusType === 'success' || redirectedResult === 'request') && stashedPayload) {
        this.persistPendingStatus(stashedPayload);
        this.activeBarterStatus = 'pending';
        this.isSubmissionLocked = true;
        this.renderPendingStatusBanner(
          stashedPayload,
          statusEl.textContent.trim() ||
            `Thank you. Your request is in progress. We’ll respond to ${stashedPayload.customer?.email || this.customerEmail} within 1–2 business days.`
        );
        statusEl.hidden = true;
        if (redirectedResult === 'request') clearBarterContactResult();
      }

      if (statusType === 'success' || statusType === 'error' || stashedPayload) {
        this.clearStashedPendingSubmission();
      }
    }

    // ─── Submission ────────────────────────────────────────────────────────

    buildRequestPayload() {
      const nameInput = this.select('[data-role="contact-name"]');
      const emailInput = this.select('[data-role="contact-email"]');
      const phoneInput = this.select('[data-role="contact-phone"]');
      const notesInput = this.select('[data-role="contact-notes"]');
      const offerInput = this.select('.wl-barter__offer-input');
      const offerCents = this.getResolvedOfferCents();
      const offerSource = offerInput?.dataset.offerSource === 'manual' ? 'manual' : 'auto';
      const projectMessage = (notesInput?.value || '').trim();
      const projectFiles = this.getProjectFileSummaries();

      const items = [];
      let originalTotalCents = 0;
      let suggestedItemsTotalCents = 0;
      let itemCount = 0;

      this.selectAll('.wl-barter-item').forEach((item) => {
        const cb = item.querySelector('[data-role="item-select"]');
        if (cb && !cb.checked) return;
        if (this.isExcludedHandle((item.dataset.productHandle || '').trim().toLowerCase())) return;

        const title = item.querySelector('.wl-barter-item__title')?.textContent?.trim() || 'Product';
        const quantity = Math.max(1, parseInt(item.querySelector('[data-role="item-quantity"]')?.value || '1', 10));
        const originalUnitPriceCents = this.normalizePrice(item.dataset.listPrice || item.dataset.unitPrice);
        const suggestedUnitPriceCents = this.normalizePrice(item.dataset.unitPrice);
        const originalLinePriceCents = originalUnitPriceCents * quantity;
        const suggestedLinePriceCents = suggestedUnitPriceCents * quantity;
        const variantTitle = (item.dataset.variantTitle || '').trim();

        originalTotalCents += originalLinePriceCents;
        suggestedItemsTotalCents += suggestedLinePriceCents;
        itemCount += quantity;

        items.push({
          product_title: title,
          variant_title: variantTitle === 'Default Title' ? '' : variantTitle,
          quantity,
          sku: item.dataset.sku || '',
          product_id: item.dataset.productId || '',
          variant_id: item.dataset.variantId || '',
          product_handle: item.dataset.productHandle || '',
          source: item.dataset.source || '',
          project_source_product: item.dataset.projectSourceProduct || '',
          project_line_source: item.dataset.projectLineSource || '',
          project_commerce_mode: item.dataset.projectCommerceMode || '',
          product_url: this.getItemProductUrl(item),
          image_url: this.getItemImageUrl(item),
          original_unit_price_cents: originalUnitPriceCents,
          suggested_unit_price_cents: suggestedUnitPriceCents,
          original_line_price_cents: originalLinePriceCents,
          suggested_line_price_cents: suggestedLinePriceCents,
          original_unit_price: this.centsToMoneyValue(originalUnitPriceCents),
          suggested_unit_price: this.centsToMoneyValue(suggestedUnitPriceCents),
          original_line_price: this.centsToMoneyValue(originalLinePriceCents),
          suggested_line_price: this.centsToMoneyValue(suggestedLinePriceCents),
        });
      });

      const discountCents = originalTotalCents - offerCents;
      const discountPct = originalTotalCents > 0 ? Number(((discountCents / originalTotalCents) * 100).toFixed(1)) : 0;

      return {
        schema_version: 1,
        request_type: 'barter_proposal',
        source: this.backendEndpoint && this.customerId ? 'shopify_app_proxy' : 'shopify_contact_form',
        request_id: this.createRequestId(),
        generated_at: new Date().toISOString(),
        currency: this.currency,
        customer: {
          name: (nameInput?.value || '').trim(),
          email: (emailInput?.value || '').trim(),
          phone: (phoneInput?.value || '').trim(),
          note: projectMessage,
        },
        project: {
          message: projectMessage,
          files: projectFiles,
          file_count: projectFiles.length,
          file_total_size_bytes: projectFiles.reduce((sum, file) => sum + file.size_bytes, 0),
        },
        items,
        totals: {
          item_count: itemCount,
          original_total_cents: originalTotalCents,
          item_suggested_total_cents: suggestedItemsTotalCents,
          customer_offer_total_cents: offerCents,
          customer_offer_source: offerSource,
          discount_requested_cents: discountCents,
          discount_requested_pct: discountPct,
          original_total: this.centsToMoneyValue(originalTotalCents),
          item_suggested_total: this.centsToMoneyValue(suggestedItemsTotalCents),
          customer_offer_total: this.centsToMoneyValue(offerCents),
          discount_requested: this.centsToMoneyValue(discountCents),
        },
      };
    }

    buildEmailBody(payload = this.buildRequestPayload()) {
      const customer = payload.customer;
      const totals = payload.totals;
      const project = payload.project || {};

      const lines = ['=== CUSTOM PRICING PROGRAM REQUEST ===', ''];

      // Customer info
      lines.push(`Name:  ${customer.name}`);
      lines.push(`Email: ${customer.email}`);
      if (customer.phone) {
        lines.push(`Phone: ${customer.phone}`);
      }
      lines.push('');

      // Items
      lines.push('--- Items in request ---');
      payload.items.forEach((item) => {
        const variant = item.variant_title ? ` (${item.variant_title})` : '';
        lines.push(
          `  • ${item.product_title}${variant}  ×${item.quantity}` +
          `  @ ${this.formatMoney(item.original_unit_price_cents)}` +
          ` = ${this.formatMoney(item.original_line_price_cents)}`
        );
      });

      lines.push('');
      lines.push(`Current list price:  ${this.formatMoney(totals.original_total_cents)}  (${totals.item_count} items)`);
      lines.push(`Customer's offer:    ${this.formatMoney(totals.customer_offer_total_cents)}`);
      lines.push(`Discount requested:  ${this.formatMoney(totals.discount_requested_cents)}  (${totals.discount_requested_pct.toFixed(1)}% off)`);

      if (project.message || customer.note) {
        lines.push('');
        lines.push('--- Project message ---');
        lines.push(project.message || customer.note);
      }

      if (Array.isArray(project.files) && project.files.length) {
        lines.push('');
        lines.push('--- Project files ---');
        project.files.forEach((file) => {
          const typeLabel = file.type ? `, ${file.type}` : '';
          lines.push(`  • ${file.name} (${file.size}${typeLabel})`);
        });
      }

      lines.push('');
      lines.push('--- Website import JSON ---');
      lines.push('Copy everything between the markers into the custom pricing request viewer. You can also save it as a .json file and import it there.');
      lines.push('BARTER_REQUEST_JSON_START');
      lines.push(JSON.stringify(payload, null, 2));
      lines.push('BARTER_REQUEST_JSON_END');

      lines.push('');
      lines.push('=== END OF REQUEST ===');
      return lines.join('\n');
    }

    async sendRequest() {
      const button = this.select('[data-role="submit-button"]');
      const statusEl = this.select('[data-role="submit-status"]');
      const nativeForm = this.closest('form');
      const hiddenBodyInput = nativeForm?.querySelector('[data-role="contact-body-hidden"]');
      const nativeSubmit = nativeForm?.querySelector('[data-role="native-submit"]');
      const hasBackendEndpoint = Boolean(this.backendEndpoint);
      const shouldUseBackend = hasBackendEndpoint && Boolean(this.customerId);
      const hasProjectFiles = this.getProjectFiles().length > 0;

      if (!button || button.disabled) return;

      const nameInput = this.select('[data-role="contact-name"]');
      const emailInput = this.select('[data-role="contact-email"]');
      const phoneInput = this.select('[data-role="contact-phone"]');
      const validation = this.getSubmitValidation();

      // Final validation
      if (!validation.isReady) {
        this.showStatus(
          'error',
          `Complete these before sending: ${this.formatRequirementList(validation.missingRequirements)}.`
        );
        return;
      }

      if (!shouldUseBackend && (!nativeForm || !hiddenBodyInput)) {
        this.showStatus('error', 'This custom pricing form is missing its Shopify contact form wrapper.');
        return;
      }

      nameInput.value = nameInput.value.trim();
      emailInput.value = emailInput.value.trim();
      if (phoneInput) phoneInput.value = phoneInput.value.trim();
      const payload = this.buildRequestPayload();
      if (hiddenBodyInput) hiddenBodyInput.value = this.buildEmailBody(payload);

      button.disabled = true;
      button.textContent = 'Sending…';
      this.showStatus('info', 'Sending your request…');
      if (statusEl) statusEl.hidden = true;

      if (nativeForm && typeof nativeForm.reportValidity === 'function' && !nativeForm.reportValidity()) {
        button.disabled = false;
        button.textContent = 'Send custom pricing request';
        this.updateSubmitButton();
        this.clearStashedPendingSubmission();
        return;
      }

      if (shouldUseBackend) {
        try {
          const result = await this.saveProposalToBackend(payload);
          this.completeBackendSubmission(payload, result);
          return;
        } catch (error) {
          console.error(error);
          try {
            const fallbackResult = await this.saveRequestToBackendFallback(payload);
            this.completeBackendSubmission(payload, {
              proposal: {
                id: fallbackResult?.submission?.id || '',
                request_id: fallbackResult?.submission?.request_id || payload.request_id,
                status: fallbackResult?.submission?.status || 'new',
                source: 'backend_submission_fallback',
              },
            });
            return;
          } catch (fallbackError) {
            console.error(fallbackError);
          }

          if (nativeForm && hiddenBodyInput && !hasProjectFiles) {
            this.showStatus('info', 'The backend did not accept the request, so we are sending it by email instead...');
            this.submitNativeContactRequest(nativeForm, nativeSubmit, payload);
            return;
          }

          button.disabled = false;
          button.textContent = 'Send custom pricing request';
          this.showStatus('error', error.message || 'We could not save your custom pricing request yet. Please try again in a moment.');
          this.clearStashedPendingSubmission();
          return;
        }
      }

      this.submitNativeContactRequest(nativeForm, nativeSubmit, payload);
    }

    submitNativeContactRequest(nativeForm, nativeSubmit, payload) {
      this.stashPendingSubmission(payload);
      stashBarterContactReturn('request');

      if (typeof nativeForm.requestSubmit === 'function') {
        nativeForm.requestSubmit();
        return;
      }

      if (nativeSubmit) {
        nativeSubmit.click();
        return;
      }

      nativeForm.submit();
    }

    completeBackendSubmission(payload, result = {}) {
      const submittedPayload = {
        ...payload,
        backend: result?.proposal || null,
      };
      const responseEmail = submittedPayload.customer?.email || this.customerEmail;
      const message = `Thank you. Your request is in progress. We’ll respond to ${responseEmail} within 1–2 business days.`;

      this.persistPendingStatus(submittedPayload);
      this.renderPendingStatusBanner(submittedPayload, message);
      this.clearStashedPendingSubmission();
      this.activeBarterStatus = 'pending';
      this.isSubmissionLocked = true;
      this.showStatus('success', message);
      this.updateSubmitButton();
    }

    async saveProposalToBackend(payload) {
      const files = this.getProjectFiles();
      const requestInit = {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      };

      if (files.length) {
        const formData = new FormData();
        formData.append('payload', JSON.stringify(payload));
        files.forEach((file) => {
          formData.append('project_files', file, file.name || 'project-file');
        });
        requestInit.body = formData;
      } else {
        requestInit.headers['content-type'] = 'application/json';
        requestInit.body = JSON.stringify(payload);
      }

      const response = await fetch(this.backendEndpoint, requestInit);
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        const error = new Error(result.error || `Custom pricing request returned ${response.status}`);
        error.status = response.status;
        throw error;
      }

      return result;
    }

    async saveRequestToBackendFallback(payload) {
      const files = this.getProjectFiles();
      const fallbackEndpoint = '/apps/barter/api/submissions/custom-pricing';
      const fallbackPayload = {
        request_id: `${payload.request_id || this.createRequestId()}-fallback`,
        submission_type: 'custom_pricing',
        source_url: window.location.href,
        customer: payload.customer,
        message: this.buildEmailBody(payload),
        fields: {
          request_type: 'custom_pricing_request',
          original_request_id: payload.request_id || '',
          fallback_reason: 'proposal_endpoint_rejected',
        },
        files: payload.project?.files || [],
        file_count: payload.project?.file_count || 0,
        file_total_size_bytes: payload.project?.file_total_size_bytes || 0,
      };
      const requestInit = {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      };

      if (files.length) {
        const formData = new FormData();
        formData.append('payload', JSON.stringify(fallbackPayload));
        files.forEach((file) => {
          formData.append('files', file, file.name || 'project-file');
        });
        requestInit.body = formData;
      } else {
        requestInit.headers['content-type'] = 'application/json';
        requestInit.body = JSON.stringify(fallbackPayload);
      }

      const response = await fetch(fallbackEndpoint, requestInit);
      const result = await response.json().catch(() => ({}));

      if (!response.ok || !result.ok) {
        const error = new Error(result.error || `Custom pricing fallback returned ${response.status}`);
        error.status = response.status;
        throw error;
      }

      return result;
    }

    showStatus(type, message) {
      const statusEl = this.select('[data-role="submit-status"]');
      if (!statusEl) return;
      statusEl.hidden = false;
      statusEl.dataset.statusType = type;
      statusEl.className = `wl-barter__submit-status wl-barter__submit-status--${type}`;
      statusEl.textContent = message;
    }

    hideStatusIfNotSuccess() {
      const statusEl = this.select('[data-role="submit-status"]');
      if (!statusEl || statusEl.dataset.statusType === 'success') return;
      statusEl.hidden = true;
      statusEl.textContent = '';
      delete statusEl.dataset.statusType;
      statusEl.className = 'wl-barter__submit-status';
    }

    initializeStatusState() {
      const slot = this.getStatusSlot();
      if (!slot) return;

      const serverBanner = slot.querySelector('.wl-barter__status-banner');
      if (serverBanner) {
        this.activeBarterStatus = this.getStatusFromBanner(serverBanner);
        this.isSubmissionLocked = ['counter', 'pending'].includes(this.activeBarterStatus);
        if (this.activeBarterStatus !== 'pending') this.clearPersistedPendingStatus();
        this.updateSubmitButton();
        return;
      }

      const pendingStatus = this.readPersistedPendingStatus();
      if (!pendingStatus) return;

      this.activeBarterStatus = 'pending';
      this.isSubmissionLocked = true;
      this.renderPendingStatusBanner(pendingStatus.payload, pendingStatus.message);
      this.updateSubmitButton();
    }

    async loadBackendProposalStatus() {
      if (!this.statusEndpoint) return;

      try {
        const response = await fetch(this.statusEndpoint, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.ok || !result.proposal) return;

        this.renderBackendProposalStatus(result.proposal);
      } catch (error) {
        console.error(error);
      }
    }

    startBackendStatusPolling() {
      if (!this.statusEndpoint || this.statusPollTimer) return;

      this.statusPollTimer = window.setInterval(() => {
        if (document.hidden) return;
        this.loadBackendProposalStatus();
      }, 30000);
    }

    clearBackendStatusPolling() {
      if (!this.statusPollTimer) return;
      window.clearInterval(this.statusPollTimer);
      this.statusPollTimer = null;
    }

    getStatusSlot() {
      return document.querySelector('[data-role="barter-status-slot"]');
    }

    getStatusFromBanner(banner) {
      if (!banner) return '';
      if (banner.classList.contains('wl-barter__status-banner--accepted')) return 'accepted';
      if (banner.classList.contains('wl-barter__status-banner--rejected')) return 'rejected';
      if (banner.classList.contains('wl-barter__status-banner--counter')) return 'counter';
      if (banner.classList.contains('wl-barter__status-banner--pending')) return 'pending';
      return '';
    }

    unlockAfterCounterDecision(status = 'accepted') {
      this.activeBarterStatus = status;
      this.isSubmissionLocked = false;
      this.clearPersistedPendingStatus();
      this.updateSubmitButton();
    }

    getPendingStorageKey() {
      if (this.customerId) return `barter-request-status:${this.customerId}`;
      if (this.customerEmail) return `barter-request-status:${this.customerEmail}`;
      return '';
    }

    persistPendingStatus(payload) {
      const storageKey = this.getPendingStorageKey();
      if (!storageKey) return;

      const record = {
        message: `Thank you. Your request is in progress. We’ll respond to ${payload.customer.email} within 1–2 business days.`,
        savedAt: new Date().toISOString(),
        payload: {
          customer: {
            email: payload.customer.email,
          },
          totals: {
            original_total_cents: payload.totals.original_total_cents,
            customer_offer_total_cents: payload.totals.customer_offer_total_cents,
          },
        },
      };

      try {
        window.localStorage.setItem(storageKey, JSON.stringify(record));
      } catch (_unusedError) {
        // Ignore localStorage failures and continue with the in-memory UI state.
      }
    }

    readPersistedPendingStatus() {
      const storageKey = this.getPendingStorageKey();
      if (!storageKey) return null;

      try {
        const rawValue = window.localStorage.getItem(storageKey);
        return rawValue ? JSON.parse(rawValue) : null;
      } catch (_unusedError) {
        return null;
      }
    }

    clearPersistedPendingStatus() {
      const storageKey = this.getPendingStorageKey();
      if (!storageKey) return;

      try {
        window.localStorage.removeItem(storageKey);
      } catch (_unusedError) {
        // Ignore localStorage failures.
      }
    }

    renderPendingStatusBanner(payload, message) {
      const slot = this.getStatusSlot();
      if (!slot) return;

      const updatedLabel = new Date().toLocaleDateString(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      });
      const listPrice = payload?.totals?.original_total_cents
        ? this.formatMoney(payload.totals.original_total_cents)
        : '';
      const offerPrice = payload?.totals?.customer_offer_total_cents
        ? this.formatMoney(payload.totals.customer_offer_total_cents)
        : '';

      slot.innerHTML = `
        <div class="wl-barter__status-banner wl-barter__status-banner--pending" role="region" aria-label="Your custom pricing request status">
          <div class="wl-barter__status-banner-head">
            <span class="wl-barter__status-badge" aria-hidden="true">…</span>
            <h2 class="wl-barter__status-banner-title">Custom pricing under review</h2>
            <span class="wl-barter__status-date">Updated ${this.escapeHtml(updatedLabel)}</span>
          </div>
          <div class="wl-barter__status-body">
            ${
              listPrice || offerPrice
                ? `<div class="wl-barter__status-amounts">
                    ${
                      listPrice
                        ? `<div class="wl-barter__status-amount-item">
                            <span class="wl-barter__status-amount-label">List price</span>
                            <span class="wl-barter__status-amount-value">${this.escapeHtml(listPrice)}</span>
                          </div>`
                        : ''
                    }
                    ${
                      offerPrice
                        ? `<div class="wl-barter__status-amount-item">
                            <span class="wl-barter__status-amount-label">Your offer</span>
                            <span class="wl-barter__status-amount-value">${this.escapeHtml(offerPrice)}</span>
                          </div>`
                        : ''
                    }
                  </div>`
                : ''
            }
            <div class="wl-barter__status-note">
              <span class="wl-barter__status-note-label">Thank you</span>
              <p class="wl-barter__status-note-body">${this.escapeHtml(message)}</p>
            </div>
            <p class="wl-barter__status-action-hint">
              We typically respond within 1–2 business days. We’ll email you at the address you provided.
            </p>
          </div>
        </div>
      `;
    }

    renderBackendProposalStatus(proposal) {
      const slot = this.getStatusSlot();
      if (!slot) return;

      const status = this.normalizeBackendStatus(proposal.status);
      if (!status) return;

      const bannerConfig = this.getBackendStatusConfig(status);
      const listPrice = this.formatMoney(proposal.original_total_cents || 0);
      const offerPrice = this.formatMoney(proposal.customer_offer_total_cents || 0);
      const counterPrice = proposal.counter_amount_cents ? this.formatMoney(proposal.counter_amount_cents) : '';
      const updatedLabel = this.formatBackendDate(proposal.updated_at);
      const adminNote = (proposal.admin_note || '').trim();
      const discountCode = (proposal.discount_code || '').trim();
      const customerResponse = proposal.payload?.customer_response || null;

      this.activeBarterStatus = status;
      this.isSubmissionLocked = ['pending', 'counter', 'customer_countered', 'customer_accepted'].includes(status);
      if (status !== 'pending') this.clearPersistedPendingStatus();

      slot.innerHTML = `
        <div class="wl-barter__status-banner wl-barter__status-banner--${this.escapeAttribute(bannerConfig.modifier)}" role="region" aria-label="Your custom pricing request status">
          <div class="wl-barter__status-banner-head">
            <span class="wl-barter__status-badge" aria-hidden="true">${this.escapeHtml(bannerConfig.icon)}</span>
            <h2 class="wl-barter__status-banner-title">${this.escapeHtml(bannerConfig.title)}</h2>
            ${updatedLabel ? `<span class="wl-barter__status-date">Updated ${this.escapeHtml(updatedLabel)}</span>` : ''}
          </div>
          <div class="wl-barter__status-body">
            <div class="wl-barter__status-amounts">
              <div class="wl-barter__status-amount-item">
                <span class="wl-barter__status-amount-label">List price</span>
                <span class="wl-barter__status-amount-value">${this.escapeHtml(listPrice)}</span>
              </div>
              <div class="wl-barter__status-amount-item">
                <span class="wl-barter__status-amount-label">Your offer</span>
                <span class="wl-barter__status-amount-value">${this.escapeHtml(offerPrice)}</span>
              </div>
              ${
                counterPrice
                  ? `<div class="wl-barter__status-amount-item wl-barter__status-amount-item--highlight">
                      <span class="wl-barter__status-amount-label">Our counter</span>
                      <span class="wl-barter__status-amount-value">${this.escapeHtml(counterPrice)}</span>
                    </div>`
                  : ''
              }
            </div>
            ${
              discountCode
                ? `<div class="wl-barter__status-note">
                    <span class="wl-barter__status-note-label">Discount code</span>
                    <p class="wl-barter__status-note-body"><strong>${this.escapeHtml(discountCode)}</strong></p>
                    ${this.renderDiscountActionControls(proposal, discountCode)}
                  </div>`
                : ''
            }
            <div class="wl-barter__status-note">
              <span class="wl-barter__status-note-label">${this.escapeHtml(bannerConfig.noteLabel)}</span>
              <p class="wl-barter__status-note-body">${this.escapeHtml(adminNote || bannerConfig.message)}</p>
            </div>
            ${this.renderCustomerResponseSummary(customerResponse)}
            ${status === 'counter' ? this.renderCounterResponseControls(proposal) : ''}
            <p class="wl-barter__status-action-hint">${this.escapeHtml(bannerConfig.hint)}</p>
          </div>
        </div>
      `;

      this.updateSubmitButton();
    }

    renderDiscountActionControls(proposal, discountCode) {
      const applyUrl = (proposal.discount_apply_url || proposal.payload?.discount?.apply_url || '').trim();
      const checkoutUrl = (proposal.discount_checkout_url || proposal.payload?.discount?.checkout_url || '').trim();
      const applyLink = applyUrl
        ? `<a class="wl-barter__accept-btn" href="${this.escapeAttribute(applyUrl)}">Apply to cart</a>`
        : '';
      const checkoutLink = checkoutUrl
        ? `<a class="wl-barter__hero-action wl-barter__hero-action--secondary" href="${this.escapeAttribute(checkoutUrl)}">Checkout approved items</a>`
        : '';

      return `
        <div class="wl-barter__discount-actions">
          ${applyLink}
          ${checkoutLink}
          <button type="button" class="wl-barter__decline-btn" data-role="copy-discount-code" data-code="${this.escapeAttribute(discountCode)}">Copy code</button>
        </div>
      `;
    }

    async copyDiscountCode(button) {
      const code = (button.dataset.code || '').trim();
      if (!code) return;

      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(code);
        } else {
          const input = document.createElement('input');
          input.value = code;
          input.setAttribute('readonly', 'readonly');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
        this.showStatus('success', `Discount code ${code} copied.`);
      } catch (_unusedError) {
        this.showStatus('info', `Use discount code ${code} at checkout.`);
      }
    }

    normalizeBackendStatus(status) {
      if (status === 'accepted') return 'accepted';
      if (status === 'declined') return 'rejected';
      if (status === 'counter') return 'counter';
      if (status === 'customer_accepted') return 'customer_accepted';
      if (status === 'customer_declined') return 'customer_declined';
      if (status === 'customer_countered') return 'customer_countered';
      if (status === 'new' || status === 'reviewing') return 'pending';
      return '';
    }

    getBackendStatusConfig(status) {
      if (status === 'customer_accepted') {
        return {
          modifier: 'accepted',
          icon: '✓',
          title: 'Counter accepted',
          noteLabel: 'Your response',
          message: 'You accepted our counter offer.',
          hint: 'We will confirm the order details and send checkout instructions or a discount code.',
        };
      }

      if (status === 'customer_declined') {
        return {
          modifier: 'rejected',
          icon: '✕',
          title: 'Counter declined',
          noteLabel: 'Your response',
          message: 'You declined our counter offer.',
          hint: 'You can submit a new custom pricing request below with a revised amount.',
        };
      }

      if (status === 'customer_countered') {
        return {
          modifier: 'pending',
          icon: '…',
          title: 'Your counter was sent',
          noteLabel: 'Your response',
          message: 'Your counter offer was sent back for review.',
          hint: 'We will review your counter and respond on this page.',
        };
      }

      if (status === 'accepted') {
        return {
          modifier: 'accepted',
          icon: '✓',
          title: 'Custom pricing approved',
          noteLabel: 'Message from us',
          message: 'Your custom pricing request has been approved.',
          hint: 'Use the discount code or instructions shown here to complete your order.',
        };
      }

      if (status === 'rejected') {
        return {
          modifier: 'rejected',
          icon: '✕',
          title: 'Custom pricing declined',
          noteLabel: 'Message from us',
          message: 'We were not able to accept this custom pricing request at the requested price.',
          hint: 'You can submit a new custom pricing request below at a revised price.',
        };
      }

      if (status === 'counter') {
        return {
          modifier: 'counter',
          icon: '↔',
          title: 'Counter-offer received',
          noteLabel: 'Message from us',
          message: 'We reviewed your request and sent a counter offer.',
          hint: 'Review the counter amount above. Contact us or submit a revised custom pricing request if you want changes.',
        };
      }

      return {
        modifier: 'pending',
        icon: '…',
        title: 'Custom pricing under review',
        noteLabel: 'Thank you',
        message: 'Your request is in progress.',
        hint: 'We typically respond within 1-2 business days. We will email you at the address you provided.',
      };
    }

    renderCustomerResponseSummary(customerResponse) {
      if (!customerResponse || typeof customerResponse !== 'object') return '';

      const responseLabel =
        {
          accept: 'Accepted counter',
          reject: 'Declined counter',
          counter: 'Sent counter',
        }[customerResponse.response] || 'Your response';
      const counterAmount = customerResponse.counter_amount_cents
        ? this.formatMoney(customerResponse.counter_amount_cents)
        : '';
      const message = (customerResponse.message || '').trim();
      const defaultMessage =
        customerResponse.response === 'accept'
          ? 'You accepted the counter offer.'
          : customerResponse.response === 'reject'
            ? 'You declined the counter offer.'
            : 'Your counter was sent for review.';
      const body = [counterAmount ? `Amount: ${counterAmount}` : '', message || defaultMessage]
        .filter(Boolean)
        .join(' | ');

      return `
        <div class="wl-barter__status-note wl-barter__status-note--customer-response">
          <span class="wl-barter__status-note-label">${this.escapeHtml(responseLabel)}</span>
          <p class="wl-barter__status-note-body">${this.escapeHtml(body)}</p>
        </div>
      `;
    }

    renderCounterResponseControls(proposal) {
      const proposalId = proposal?.id || '';
      if (!proposalId) return '';

      return `
        <div class="wl-barter__counter-response" data-role="counter-response-panel" data-proposal-id="${this.escapeAttribute(proposalId)}">
          <div class="wl-barter__counter-response-head">
            <h3>Reply to this counter</h3>
            <p>Accept our counter, send the amount you can meet, or close this custom pricing request.</p>
          </div>
          <div class="wl-barter__counter-response-grid">
            <label class="wl-barter__counter-response-field">
              <span>Your counter amount</span>
              <input type="text" inputmode="decimal" autocomplete="off" data-role="counter-response-amount" placeholder="0.00">
            </label>
            <label class="wl-barter__counter-response-field">
              <span>Message</span>
              <textarea rows="3" data-role="counter-response-message" placeholder="Optional note"></textarea>
            </label>
          </div>
          <div class="wl-barter__counter-response-actions">
            <button type="button" class="wl-barter__accept-btn" data-role="counter-response-button" data-response="accept">Accept counter</button>
            <button type="button" class="wl-barter__counter-btn" data-role="counter-response-button" data-response="counter">Send my counter</button>
            <button type="button" class="wl-barter__decline-btn" data-role="counter-response-button" data-response="reject">Reject counter</button>
          </div>
        </div>
      `;
    }

    async sendCounterResponse(button) {
      const panel = button.closest('[data-role="counter-response-panel"]');
      const responseType = button.dataset.response || '';
      const proposalId = panel?.dataset.proposalId || '';
      const endpoint = this.getCustomerResponseEndpoint(proposalId);

      if (!panel || !endpoint || !['accept', 'reject', 'counter'].includes(responseType)) return;

      const amountInput = panel.querySelector('[data-role="counter-response-amount"]');
      const messageInput = panel.querySelector('[data-role="counter-response-message"]');
      const payload = {
        response: responseType,
        counter_amount_cents: null,
        message: (messageInput?.value || '').trim(),
      };

      if (responseType === 'counter') {
        const counterAmountCents = this.parseMoneyInputToCents(amountInput?.value || '');
        if (!counterAmountCents || counterAmountCents < 1) {
          if (amountInput) amountInput.focus();
          this.showStatus('error', 'Enter your counter amount before sending.');
          return;
        }
        payload.counter_amount_cents = counterAmountCents;
      }

      const originalText = button.textContent;
      this.setCounterResponseBusy(panel, true);
      button.textContent =
        responseType === 'accept' ? 'Accepting...' : responseType === 'reject' ? 'Rejecting...' : 'Sending...';

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'content-type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify(payload),
        });
        const result = await response.json().catch(() => ({}));

        if (!response.ok || !result.ok || !result.proposal) {
          throw new Error(result.error || `Counter response failed with ${response.status}`);
        }

        this.renderBackendProposalStatus(result.proposal);
        this.showStatus('success', 'Your response was sent.');
      } catch (error) {
        console.error(error);
        this.setCounterResponseBusy(panel, false);
        button.textContent = originalText;
        this.showStatus('error', error.message || 'Could not send your response.');
      }
    }

    setCounterResponseBusy(panel, isBusy) {
      panel.setAttribute('aria-busy', isBusy ? 'true' : 'false');
      panel.querySelectorAll('button, input, textarea').forEach((control) => {
        control.disabled = isBusy;
      });
    }

    getCustomerResponseEndpoint(proposalId) {
      if (!this.statusEndpoint || !proposalId) return '';
      const safeProposalId = encodeURIComponent(proposalId);
      return this.statusEndpoint.replace(/\/latest(?=($|\?))/, `/${safeProposalId}/respond`);
    }

    extractServerStatusFromHtml(html) {
      if (!html) return null;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const statusNode = parsedDocument.querySelector('[data-role="submit-status"]');
      if (!statusNode) return null;

      const statusType =
        statusNode.dataset.statusType ||
        (statusNode.classList.contains('wl-barter__submit-status--success') ? 'success' : 'error');

      return {
        type: statusType,
        message: (statusNode.textContent || '').trim(),
      };
    }

    isChallengeUrl(url) {
      try {
        return new URL(url, window.location.origin).pathname === '/challenge';
      } catch (_unusedError) {
        return false;
      }
    }

    // ─── Utilities ────────────────────────────────────────────────────────

    getItemProductUrl(item) {
      const anchor = item.querySelector('.wl-barter-item__title a');
      return this.toAbsoluteUrl(item.dataset.productUrl || anchor?.getAttribute('href') || anchor?.href || '');
    }

    getItemImageUrl(item) {
      const image = item.querySelector('img');
      return this.toAbsoluteUrl(item.dataset.imageUrl || image?.currentSrc || image?.src || '');
    }

    createRequestId() {
      if (window.crypto?.randomUUID) return `barter-${window.crypto.randomUUID()}`;
      return `barter-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    }

    toAbsoluteUrl(value) {
      const rawValue = (value || '').trim();
      if (!rawValue || rawValue === '#') return '';

      try {
        return new URL(rawValue, window.location.origin).href;
      } catch (error) {
        return rawValue;
      }
    }

    centsToMoneyValue(cents) {
      return Math.round(cents || 0) / 100;
    }

    normalizePrice(value) {
      if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);
      if (typeof value !== 'string') return 0;
      const trimmed = value.trim();
      if (!trimmed) return 0;
      const normalized = trimmed.replace(/[^0-9.-]/g, '');
      const numericValue = Number.parseFloat(normalized);
      if (Number.isNaN(numericValue)) return 0;
      return normalized.includes('.') ? Math.round(numericValue * 100) : Math.round(numericValue);
    }

    parseMoneyInputToCents(value) {
      const normalized = String(value || '').replace(/[^0-9.]/g, '');
      const numericValue = Number.parseFloat(normalized);
      return Number.isFinite(numericValue) ? Math.round(numericValue * 100) : 0;
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
      }).format((cents || 0) / 100);
    }

    formatFileSize(bytes) {
      const size = Number(bytes) || 0;
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }

    formatBackendDate(value) {
      if (!value) return '';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
      }).format(date);
    }

    getFileExtension(fileName) {
      const extension = String(fileName || '').split('.').pop();
      return extension && extension !== fileName ? `.${extension.toLowerCase()}` : '';
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    escapeAttribute(value) {
      return this.escapeHtml(value);
    }
  }

  customElements.define('barter-builder', BarterBuilder);
}

// ─── Counter-Accept Element ───────────────────────────────────────────────────
// Handles the per-item counter-offer accept/decline buttons rendered in the
// status banner. Lives outside barter-builder so it needs its own element.

if (!customElements.get('counter-accept')) {
  class CounterAccept extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.currency = this.dataset.currency || 'USD';
      this.customerName = (this.dataset.customerName || '').trim() || 'Valued Customer';
      this.customerEmail = (this.dataset.customerEmail || '').trim();
      this.counterUpdatedAt = (this.dataset.updatedAt || '').trim();

      try {
        this.items = JSON.parse(this.dataset.items || 'null') || [];
      } catch (e) {
        this.items = [];
      }

      this.mainForm = document.getElementById('BarterContactForm');
      this.mainBodyInput = this.mainForm?.querySelector('[data-role="contact-body-hidden"]') || null;
      this.mainNameInput = this.mainForm?.querySelector('[data-role="contact-name"]') || null;
      this.mainEmailInput = this.mainForm?.querySelector('[data-role="contact-email"]') || null;
      this.mainNativeSubmit = this.mainForm?.querySelector('[data-role="native-submit"]') || null;
      this.restoreAcceptedLock();
      this.recoverAcceptanceAfterReturn();
      this.addEventListener('click', this.handleClick.bind(this));
    }

    handleClick(event) {
      const acceptBtn = event.target.closest('[data-role="accept-counter"]');
      if (acceptBtn && !acceptBtn.disabled && acceptBtn.dataset.submitting !== 'true') {
        event.preventDefault();
        this.doAccept(acceptBtn);
        return;
      }

      const declineBtn = event.target.closest('[data-role="decline-counter"]');
      if (declineBtn) {
        event.preventDefault();
        const builder = document.querySelector('barter-builder');
        if (builder) {
          if (typeof builder.unlockAfterCounterDecision === 'function') {
            builder.unlockAfterCounterDecision('rejected');
          }
          builder.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    async doAccept(button) {
      // If we don't have a customer email we can't send the notification —
      // fall back to a contact link rather than a broken silent POST.
      if (!this.customerEmail) {
        this.showStatus(
          'error',
          "We couldn\u2019t find your email address. Please contact us directly to confirm your acceptance."
        );
        return false;
      }

      if (!this.mainForm || !this.mainBodyInput) {
        this.showStatus(
          'error',
          'This counter-offer form is missing its Shopify contact form wrapper.'
        );
        return false;
      }

      button.dataset.submitting = 'true';
      button.textContent = 'Sending…';
      this.showStatus('info', 'Sending your acceptance…');
      if (this.mainNameInput) this.mainNameInput.value = this.customerName;
      if (this.mainEmailInput) this.mainEmailInput.value = this.customerEmail;
      this.mainBodyInput.value = this.buildAcceptanceEmail();
      this.stashPendingAcceptance();
      stashBarterContactReturn('accept');

      if (typeof this.mainForm.reportValidity === 'function' && !this.mainForm.reportValidity()) {
        delete button.dataset.submitting;
        button.textContent = 'Accept counter-offer';
        this.clearStashedAcceptance();
        return false;
      }

      const successMessage =
        'Thank you. Your discount codes are in progress and will be sent within 24 hours.';

      try {
        await this.sendAcceptanceNotification();
      } catch (error) {
        console.error(error);
        delete button.dataset.submitting;
        button.disabled = false;
        button.textContent = 'Accept counter-offer';
        this.clearStashedAcceptance();
        this.showStatus(
          'error',
          'We could not send your acceptance. Please try again or contact us directly.'
        );
        return false;
      }

      this.persistAcceptedLock(successMessage);
      this.renderAcceptedBanner(successMessage);
      this.clearStashedAcceptance();
      return false;
    }

    async sendAcceptanceNotification() {
      const action = this.mainForm.getAttribute('action') || '/contact';
      const response = await fetch(action, {
        method: 'POST',
        body: new FormData(this.mainForm),
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html',
        },
      });

      if (!response.ok) {
        throw new Error(`Acceptance contact form returned ${response.status}`);
      }

      if (this.isChallengeUrl(response.url)) {
        throw new Error('Acceptance contact form was routed to Shopify challenge.');
      }
    }

    isChallengeUrl(url) {
      try {
        return new URL(url, window.location.origin).pathname === '/challenge';
      } catch (_unusedError) {
        return false;
      }
    }

    buildAcceptanceEmail() {
      const lines = ['=== CUSTOM PRICING COUNTER ACCEPTED ===', ''];
      lines.push(`Customer: ${this.customerName}`);
      lines.push(`Email:    ${this.customerEmail}`);
      lines.push('');

      if (this.items.length > 0) {
        lines.push('--- Accepted counter-offer breakdown ---');
        let listTotal = 0;
        let requestedTotal = 0;
        let counterTotal = 0;

        this.items.forEach((item) => {
          const listCents = this.parseMoneyValue(item.list_price);
          const requestedQty = parseInt(item.qty || item.quantity, 10) || 1;
          const counterQty = parseInt(item.counter_qty || item.counter_quantity || requestedQty, 10) || 1;
          const quantityNote = counterQty === requestedQty ? '' : ` (customer asked for ${requestedQty})`;
          const counterPct = parseFloat(item.counter_pct) || 0;
          const customerPct = parseFloat(item.customer_pct) || 0;

          let customerUnitCents = this.hasValue(item.customer_price)
            ? this.parseMoneyValue(item.customer_price)
            : 0;
          if (!this.hasValue(item.customer_price)) {
            customerUnitCents = customerPct ? listCents - Math.round(listCents * customerPct / 100) : listCents;
          }

          let counterUnitCents = this.hasValue(item.counter_price)
            ? this.parseMoneyValue(item.counter_price)
            : 0;
          if (!this.hasValue(item.counter_price)) {
            counterUnitCents = counterPct ? listCents - Math.round(listCents * counterPct / 100) : listCents;
          }

          const counterLineCents = counterUnitCents * counterQty;
          const listLineCents = listCents * counterQty;
          const requestedLineCents = customerUnitCents * requestedQty;

          listTotal += listLineCents;
          requestedTotal += requestedLineCents;
          counterTotal += counterLineCents;

          lines.push(`  • ${item.title}`);
          lines.push(`    Qty: ${counterQty}${quantityNote}`);
          lines.push(`    List: ${this.formatMoney(listLineCents)} (${counterQty} × ${this.formatMoney(listCents)})`);
          lines.push(
            `    They asked: ${this.formatMoney(requestedLineCents)} (${requestedQty} × ${this.formatMoney(customerUnitCents)})` +
            `${customerPct ? `, ${customerPct}% off` : ''}`
          );
          lines.push(
            `    Counter: ${this.formatMoney(counterLineCents)} (${counterQty} × ${this.formatMoney(counterUnitCents)})` +
            `${counterPct ? `, ${counterPct}% off` : ''}`
          );
        });

        lines.push('');
        lines.push(`List total:    ${this.formatMoney(listTotal)}`);
        lines.push(`Asked total:   ${this.formatMoney(requestedTotal)}`);
        lines.push(`Counter total: ${this.formatMoney(counterTotal)}`);
        lines.push(`Savings:       ${this.formatMoney(listTotal - counterTotal)}`);
      } else {
        lines.push('(No per-item data - see original custom pricing request email for details.)');
      }

      lines.push('');
      lines.push('ACTION REQUIRED: Generate a discount code and email it to the customer.');
      lines.push('=== END ===');

      return lines.join('\n');
    }

    getAcceptanceDraftKey() {
      if (!this.customerEmail) return '';
      return this.counterUpdatedAt
        ? `barter-counter-accept:${this.customerEmail}:${this.counterUpdatedAt}`
        : `barter-counter-accept:${this.customerEmail}`;
    }

    getAcceptedLockKey() {
      if (!this.customerEmail) return '';
      return this.counterUpdatedAt
        ? `barter-counter-accepted:${this.customerEmail}:${this.counterUpdatedAt}`
        : `barter-counter-accepted:${this.customerEmail}`;
    }

    stashPendingAcceptance() {
      const storageKey = this.getAcceptanceDraftKey();
      if (!storageKey) return;

      try {
        window.sessionStorage.setItem(
          storageKey,
          JSON.stringify({
            savedAt: new Date().toISOString(),
            customerEmail: this.customerEmail,
          })
        );
      } catch (_unusedError) {
        // Ignore sessionStorage failures.
      }
    }

    readStashedAcceptance() {
      const storageKey = this.getAcceptanceDraftKey();
      if (!storageKey) return null;

      try {
        const rawValue = window.sessionStorage.getItem(storageKey);
        return rawValue ? JSON.parse(rawValue) : null;
      } catch (_unusedError) {
        return null;
      }
    }

    clearStashedAcceptance() {
      const storageKey = this.getAcceptanceDraftKey();
      if (!storageKey) return;

      try {
        window.sessionStorage.removeItem(storageKey);
      } catch (_unusedError) {
        // Ignore sessionStorage failures.
      }
    }

    persistAcceptedLock(message) {
      const storageKey = this.getAcceptedLockKey();
      if (!storageKey) return;

      try {
        window.localStorage.setItem(
          storageKey,
          JSON.stringify({
            savedAt: new Date().toISOString(),
            message,
          })
        );
      } catch (_unusedError) {
        // Ignore localStorage failures.
      }
    }

    readAcceptedLock() {
      const storageKey = this.getAcceptedLockKey();
      if (!storageKey) return null;

      try {
        const rawValue = window.localStorage.getItem(storageKey);
        return rawValue ? JSON.parse(rawValue) : null;
      } catch (_unusedError) {
        return null;
      }
    }

    clearAcceptedLock() {
      const storageKey = this.getAcceptedLockKey();
      if (!storageKey) return;

      try {
        window.localStorage.removeItem(storageKey);
      } catch (_unusedError) {
        // Ignore localStorage failures.
      }
    }

    markAccepted() {
      const button = this.querySelector('[data-role="accept-counter"]');
      if (button) {
        button.disabled = true;
        delete button.dataset.submitting;
        button.textContent = '✓ Counter accepted';
        button.classList.add('wl-barter__accept-btn--confirmed');
      }

      const declineBtn = this.querySelector('[data-role="decline-counter"]');
      if (declineBtn) declineBtn.hidden = true;
    }

    renderAcceptedBanner(message) {
      const banner = this.closest('.wl-barter__status-banner');
      if (!banner) {
        this.markAccepted();
        this.showStatus('success', message);
        this.unlockBuilderForNewProposal('accepted');
        this.scheduleAcceptedBannerClear();
        return;
      }

      banner.classList.remove(
        'wl-barter__status-banner--counter',
        'wl-barter__status-banner--pending',
        'wl-barter__status-banner--rejected'
      );
      banner.classList.add('wl-barter__status-banner--accepted');

      const badge = banner.querySelector('.wl-barter__status-badge');
      if (badge) badge.textContent = '✓';

      const title = banner.querySelector('.wl-barter__status-banner-title');
      if (title) title.textContent = 'Custom pricing approved';

      const body = banner.querySelector('.wl-barter__status-body');
      if (body) {
        body.innerHTML = `
          <div class="wl-barter__status-note">
            <span class="wl-barter__status-note-label">Thank you</span>
            <p class="wl-barter__status-note-body">${this.escapeHtml(message)}</p>
          </div>
          <p class="wl-barter__status-action-hint">
            Your discount codes are in progress and will be sent within 24 hours.
          </p>
        `;
      }

      this.unlockBuilderForNewProposal('accepted');
      this.scheduleAcceptedBannerClear(banner);
    }

    scheduleAcceptedBannerClear(banner = this.closest('.wl-barter__status-banner')) {
      window.setTimeout(() => {
        this.clearAcceptedBanner(banner);
      }, BARTER_ACCEPTED_BANNER_DURATION_MS);
    }

    clearAcceptedBanner(banner = this.closest('.wl-barter__status-banner')) {
      if (banner) {
        banner.remove();
      } else {
        const slot = document.querySelector('[data-role="barter-status-slot"]');
        if (slot) slot.innerHTML = '';
      }

      this.unlockBuilderForNewProposal('accepted');
    }

    unlockBuilderForNewProposal(status = 'accepted') {
      const builder = document.querySelector('barter-builder');
      if (!builder || typeof builder.unlockAfterCounterDecision !== 'function') return;
      builder.unlockAfterCounterDecision(status);
    }

    restoreAcceptedLock() {
      const acceptedLock = this.readAcceptedLock();
      if (!acceptedLock) return;

      this.clearAcceptedBanner();
    }

    recoverAcceptanceAfterReturn() {
      const serverStatus = document.querySelector('[data-role="submit-status"]');
      const stashedAcceptance = this.readStashedAcceptance();
      if (!serverStatus) {
        if (stashedAcceptance) this.clearStashedAcceptance();
        return;
      }

      const statusType = serverStatus.dataset.statusType || '';
      const redirectedResult = getBarterContactResult();

      if ((statusType === 'success' || redirectedResult === 'accept') && stashedAcceptance) {
        const successMessage =
          'Thank you. Your discount codes are in progress and will be sent within 24 hours.';
        this.persistAcceptedLock(successMessage);
        this.renderAcceptedBanner(successMessage);
        serverStatus.hidden = true;
        this.clearStashedAcceptance();
        if (redirectedResult === 'accept') clearBarterContactResult();
        return;
      }

      if (statusType === 'error') {
        const button = this.querySelector('[data-role="accept-counter"]');
        if (button) {
          button.disabled = false;
          delete button.dataset.submitting;
          button.textContent = 'Accept counter-offer';
        }
        this.showStatus(
          'error',
          'Something went wrong. Please try again or contact us directly.'
        );
        serverStatus.hidden = true;
        this.clearStashedAcceptance();
      }
    }

    hasValue(value) {
      return value !== null && value !== undefined && String(value).trim() !== '';
    }

    parseMoneyValue(value) {
      if (!this.hasValue(value)) return 0;
      if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value);

      const normalized = String(value).trim().replace(/[^0-9.-]/g, '');
      if (!normalized) return 0;

      const numericValue = Number.parseFloat(normalized);
      if (Number.isNaN(numericValue)) return 0;

      return normalized.includes('.') ? Math.round(numericValue * 100) : Math.round(numericValue);
    }

    formatMoney(cents) {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.currency,
        minimumFractionDigits: 2,
      }).format((cents || 0) / 100);
    }

    showStatus(type, message) {
      const statusEl = this.querySelector('[data-role="accept-status"]');
      if (!statusEl) return;
      statusEl.hidden = false;
      statusEl.className = `wl-barter__accept-status wl-barter__accept-status--${type}`;
      statusEl.textContent = message;
    }

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('counter-accept', CounterAccept);
}

// ─── Test Panel ───────────────────────────────────────────────────────────────
// Lets admins preview every proposal state without touching metafields.
// Injects a mock banner into [data-role="barter-status-slot"].

(function initTestPanel() {
  if (!document.querySelector('[data-role="test-state"]')) return;

  const MOCK_ITEMS = [
    { title: 'LED B10 40W Dimmable', qty: 2, list_price: 19.99, customer_pct: 10, counter_pct: 9 },
    { title: 'Switch 15A Single Pole', qty: 1, counter_qty: 2, list_price: 8.50, customer_pct: 15, counter_pct: 12 },
    { title: 'Wire 12AWG Romex 10ft', qty: 3, list_price: 5.25, customer_pct: 5, counter_pct: 2 },
  ];

  const CURRENCY = document.querySelector('barter-builder')?.dataset?.currency || 'USD';

  function fmt(cents) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: CURRENCY, minimumFractionDigits: 2,
    }).format(cents / 100);
  }

  function buildCounterTable() {
    let listTotal = 0, requestedTotal = 0, counterTotal = 0;
    const rows = MOCK_ITEMS.map((item) => {
      const listCents = Math.round(item.list_price * 100);
      const requestedQty = parseInt(item.qty || item.quantity, 10) || 1;
      const counterQty = parseInt(item.counter_qty || item.counter_quantity || requestedQty, 10) || 1;
      const quantityNote = counterQty === requestedQty ? '' : `<span class="wl-barter__counter-subline">asked ${requestedQty}</span>`;
      const listLine = listCents * counterQty;
      const askedUnit = listCents - Math.round(listCents * item.customer_pct / 100);
      const askedLine = askedUnit * requestedQty;
      const counterUnit = listCents - Math.round(listCents * item.counter_pct / 100);
      const ctrLine = counterUnit * counterQty;
      listTotal += listLine;
      requestedTotal += askedLine;
      counterTotal += ctrLine;
      return `<tr class="wl-barter__counter-row">
        <td class="wl-barter__counter-td">${item.title}</td>
        <td class="wl-barter__counter-td wl-barter__counter-td--num">${counterQty}${quantityNote}</td>
        <td class="wl-barter__counter-td wl-barter__counter-td--num">${fmt(listLine)}<span class="wl-barter__counter-subline">${counterQty} × ${fmt(listCents)}</span></td>
        <td class="wl-barter__counter-td wl-barter__counter-td--num wl-barter__counter-td--muted">${fmt(askedLine)}<span class="wl-barter__counter-subline">${requestedQty} × ${fmt(askedUnit)} · ${item.customer_pct}% off</span></td>
        <td class="wl-barter__counter-td wl-barter__counter-td--num wl-barter__counter-td--highlight">${fmt(ctrLine)}<span class="wl-barter__counter-subline">${counterQty} × ${fmt(counterUnit)} · ${item.counter_pct}% off</span></td>
      </tr>`;
    }).join('');
    const foot = `<tr class="wl-barter__counter-row wl-barter__counter-row--total">
      <td class="wl-barter__counter-td" colspan="2"><strong>Total</strong></td>
      <td class="wl-barter__counter-td wl-barter__counter-td--num"><strong>${fmt(listTotal)}</strong></td>
      <td class="wl-barter__counter-td wl-barter__counter-td--num"><strong>${fmt(requestedTotal)}</strong></td>
      <td class="wl-barter__counter-td wl-barter__counter-td--num"><strong>${fmt(counterTotal)}</strong></td>
    </tr>`;
    return `<div class="wl-barter__counter-wrap">
      <table class="wl-barter__counter-table">
        <thead><tr>
          <th class="wl-barter__counter-th">Item</th>
          <th class="wl-barter__counter-th wl-barter__counter-th--num">Qty</th>
          <th class="wl-barter__counter-th wl-barter__counter-th--num">List</th>
          <th class="wl-barter__counter-th wl-barter__counter-th--num">You asked</th>
          <th class="wl-barter__counter-th wl-barter__counter-th--num">Our counter</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>${foot}</tfoot>
      </table>
    </div>`;
  }

  const MOCK_BANNERS = {
    pending: `
      <div class="wl-barter__status-banner wl-barter__status-banner--pending" role="region">
        <div class="wl-barter__status-banner-head">
          <span class="wl-barter__status-badge" aria-hidden="true">…</span>
          <h2 class="wl-barter__status-banner-title">Custom pricing under review</h2>
          <span class="wl-barter__status-date">Updated Apr 08, 2026</span>
        </div>
        <div class="wl-barter__status-body">
          <p class="wl-barter__status-action-hint">We typically respond within 1–2 business days. We'll email you at the address you provided.</p>
        </div>
      </div>`,

    accepted: `
      <div class="wl-barter__status-banner wl-barter__status-banner--accepted" role="region">
        <div class="wl-barter__status-banner-head">
          <span class="wl-barter__status-badge" aria-hidden="true">✓</span>
          <h2 class="wl-barter__status-banner-title">Custom pricing approved</h2>
          <span class="wl-barter__status-date">Updated Apr 08, 2026</span>
        </div>
        <div class="wl-barter__status-body">
          <div class="wl-barter__status-note">
            <span class="wl-barter__status-note-label">Message from us</span>
            <p class="wl-barter__status-note-body">Great news — we've approved your custom pricing request. Your discount code is on its way to your email within the hour.</p>
          </div>
          <p class="wl-barter__status-action-hint">Check your email — we'll send a discount code or instructions to complete your order. Questions? <a href="/pages/contact">Contact us</a>.</p>
        </div>
      </div>`,

    rejected: `
      <div class="wl-barter__status-banner wl-barter__status-banner--rejected" role="region">
        <div class="wl-barter__status-banner-head">
          <span class="wl-barter__status-badge" aria-hidden="true">✕</span>
          <h2 class="wl-barter__status-banner-title">Custom pricing declined</h2>
          <span class="wl-barter__status-date">Updated Apr 08, 2026</span>
        </div>
        <div class="wl-barter__status-body">
          <div class="wl-barter__status-note">
            <span class="wl-barter__status-note-label">Message from us</span>
            <p class="wl-barter__status-note-body">We weren't able to accept this custom pricing request at the requested price. You're welcome to submit a revised offer below.</p>
          </div>
          <p class="wl-barter__status-action-hint">You can submit a new custom pricing request below at a revised price. <a href="/pages/contact">Contact us</a> if you have questions.</p>
        </div>
      </div>`,

    counter: `
      <div class="wl-barter__status-banner wl-barter__status-banner--counter" role="region">
        <div class="wl-barter__status-banner-head">
          <span class="wl-barter__status-badge" aria-hidden="true">↔</span>
          <h2 class="wl-barter__status-banner-title">Counter-offer received</h2>
          <span class="wl-barter__status-date">Updated Apr 08, 2026</span>
        </div>
        <div class="wl-barter__status-body">
          ${buildCounterTable()}
          <div class="wl-barter__status-note">
            <span class="wl-barter__status-note-label">Message from us</span>
            <p class="wl-barter__status-note-body">Here's our best offer on these items. Accept below and we'll email you a discount code within 1–2 business days.</p>
          </div>
          <div class="wl-barter__counter-accept">
            <div class="wl-barter__counter-actions">
              <button type="button" class="wl-barter__accept-btn" disabled>Accept counter-offer (demo)</button>
              <button type="button" class="wl-barter__decline-btn">Decline — submit new request</button>
            </div>
          </div>
        </div>
      </div>`,
  };

  // Store the original slot HTML so "Clear" restores it
  let originalSlotHTML = null;

  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-role="test-state"]');
    if (!btn) return;
    event.preventDefault();

    const slot = document.querySelector('[data-role="barter-status-slot"]');
    if (!slot) return;

    if (originalSlotHTML === null) originalSlotHTML = slot.innerHTML;

    const state = btn.dataset.state;

    if (state === 'clear') {
      slot.innerHTML = originalSlotHTML;
      // Re-attach counter-accept elements if any exist in the restored HTML
      // (Custom elements re-connect automatically when added back to the DOM)
    } else if (MOCK_BANNERS[state]) {
      slot.innerHTML = MOCK_BANNERS[state];
    }

    // Highlight the active test button
    document.querySelectorAll('[data-role="test-state"]').forEach((b) => b.classList.remove('wl-barter__test-btn--active'));
    if (state !== 'clear') btn.classList.add('wl-barter__test-btn--active');
  });
})();
